// noinspection SpellCheckingInspection
import { Injectable, InjectionToken, Injector, inject } from '@angular/core';

import {
    DslEnvService,
    EVENT_PROCESSOR,
    EventContext,
    FEATURE_INIT_PROVIDER,
    LAUNCH_DARKLY_LAZY_CONTEXT_PROVIDER,
    LAZY_DSL_VALUES_PROVIDER,
    Logger,
} from '@frontend/vanilla/core';
import { CriticalErrorServiceServer, ROUTE_EXPLORATION } from '@frontend/vanilla/ssr';

/**
 * @experimental
 */
@Injectable({
    providedIn: 'root',
})
export class ModuleService {
    private dslEnvService = inject(DslEnvService);
    private log = inject(Logger);

    private serverRenderErrorService = inject(CriticalErrorServiceServer);

    getProviders<T>(injector: Injector, token: InjectionToken<T>, notFoundValue?: T[]): T[] {
        try {
            return injector.get<T[]>(token, notFoundValue);
        } catch (e) {
            this.log.info('getProviders<T> failed for injector with token', injector, token, e);
            return [];
        }
    }

    getProvider<T>(injector: Injector, token: InjectionToken<T>, notFoundValue?: T): T | null {
        try {
            return injector.get(token, notFoundValue);
        } catch (e) {
            this.log.info('getProvider failed with token', injector, token, e);
            return null;
        }
    }

    private isRouteExploration = inject(ROUTE_EXPLORATION);

    runBootstrappers(injector: Injector) {
        if (this.isRouteExploration) {
            return;
        }
        try {
            const bootstrappers = this.getProviders(injector, FEATURE_INIT_PROVIDER, []);

            for (const bootstrapper of bootstrappers) {
                bootstrapper.onFeatureInit();
            }
        } catch (e) {
            this.serverRenderErrorService.reportCriticalError(`runBootstrappers failed for injector`);
            this.log.error('runBootstrappers failed for injector', injector, e);
        }
    }

    runEventProcessors(injector: Injector, context: EventContext<any>) {
        try {
            const eventProcessors = this.getProviders(injector, EVENT_PROCESSOR, []);

            for (const eventProcessor of eventProcessors) {
                eventProcessor.process(context);
            }
        } catch (e) {
            this.log.error('runEventHandlers failed with context', injector, context, e);
        }
    }

    runDslProviders(injector: Injector) {
        try {
            const dslProviders = this.getProvider(injector, LAZY_DSL_VALUES_PROVIDER, null);

            if (dslProviders?.length) {
                this.dslEnvService.addToContext([...dslProviders]);
            }
        } catch (e) {
            this.log.error('runDslProviders failed', injector, e);
        }
    }

    runLaunchDarklyContextProvider(injector: Injector) {
        try {
            const contextProvider = this.getProvider(injector, LAUNCH_DARKLY_LAZY_CONTEXT_PROVIDER, null);

            if (contextProvider) {
                contextProvider.reloadContext();
            }
        } catch (e) {
            this.log.error('runLaunchDarklyContextProvider failed', injector, e);
        }
    }
}
